import { useState, useEffect } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";

export default function ReminderSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const { toast } = useToast();

  const load = () => api.get("/reminders/settings").then(r => setSettings(r.data)).catch(() => setSettings(null));

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.put("/reminders/settings", { enabled: !settings.enabled });
      toast({ title: !settings.enabled ? "Relances automatiques activées" : "Relances automatiques désactivées" });
      await load();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    } finally { setBusy(false); }
  };

  const run = async (dryRun) => {
    if (!dryRun && !window.confirm("Envoyer réellement les emails de relance maintenant ?")) return;
    setBusy(true);
    try {
      const res = await api.post(`/reminders/run?dry_run=${dryRun}`);
      setReport(res.data);
      toast({ title: dryRun ? "Simulation terminée" : "Relances envoyées" });
      if (!dryRun) await load();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur lors de l'exécution" });
    } finally { setBusy(false); }
  };

  if (!settings) return null;

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="reminder-settings-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Relances automatiques
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Rappels d'échéances (J-3, jour J, J+2), relance des élèves inactifs 7 jours, et point quotidien envoyé aux Majors.
            <br />Planification : {settings.schedule}
          </p>
        </div>
        <button
          data-testid="reminders-enabled-toggle"
          onClick={toggle}
          disabled={busy}
          aria-pressed={settings.enabled}
          className={`relative shrink-0 w-12 h-6 rounded-full transition-colors duration-200 ${settings.enabled ? "bg-emerald-500" : "bg-gray-300"} disabled:opacity-50`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${settings.enabled ? "translate-x-6" : "translate-x-0"}`} />
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Dernière exécution : {settings.last_run ? new Date(settings.last_run).toLocaleString("fr-FR") : "jamais"}
      </p>

      <div className="flex flex-wrap gap-3">
        <button data-testid="reminders-dry-run-btn" onClick={() => run(true)} disabled={busy} className="btn-secondary disabled:opacity-50">
          {busy ? "..." : "Simuler (sans envoi)"}
        </button>
        <button data-testid="reminders-run-btn" onClick={() => run(false)} disabled={busy} className="btn-primary disabled:opacity-50">
          Lancer maintenant
        </button>
      </div>

      {report && (
        <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4" data-testid="reminders-report">
          <p className="text-sm font-medium text-gray-900 mb-2">
            {report.dry_run ? "Simulation" : "Exécution"} : {report.deadline_emails} échéance(s), {report.inactivity_emails} relance(s) d'inactivité, {report.coach_digests} point(s) coach
          </p>
          <ul className="text-xs text-gray-600 space-y-1 max-h-48 overflow-auto">
            {(report.details || []).length === 0
              ? <li className="text-gray-400">Aucune relance due aujourd'hui (tous les rappels déjà envoyés ou aucune échéance concernée).</li>
              : report.details.map((d, i) => <li key={`${d}-${i}`}>• {d}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
